/**
 * Login Page - Redesigned with Sétima Branding
 */

import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Stack,
  Grid,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { touchFriendlyButton, touchFriendlyTextField } from '../../utils/mobileFriendlyStyles';
import AppFooter from '../AppFooter';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/historico');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao fazer login';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#121212',
      }}
    >
      <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={0} sx={{ minHeight: { md: '80vh' } }}>
          {/* Coluna Esquerda - Branding Sétima */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 4, md: 6 },
              color: 'white',
            }}
          >
            {/* Logo oficial (arquivo público) */}
            <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src="/setima-logo.svg"
                alt="Sétima"
                sx={{ width: { xs: 153, md: 204 }, height: 'auto' }}
              />
            </Box>

            {/* Headline em duas linhas (sem hífen) */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 300,
                  fontSize: { xs: '1.6rem', md: '2.08rem' }, // 20% menor (2rem -> 1.6rem, 2.6rem -> 2.08rem)
                  letterSpacing: '0.01em',
                  lineHeight: 1.2,
                  color: 'rgba(255,255,255,0.95)',
                }}
                >
                Valuation Platform
                </Typography>
              <></>
            </Box>

            {/* Subtítulo */}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                maxWidth: '640px',
                fontSize: { xs: '1rem', md: '1.05rem' },
                lineHeight: 1.75,
                color: 'rgba(255, 255, 255, 0.82)',
                fontWeight: 300,
                mt: 1,
              }}
            >
              Sistema para acompanhar métricas, premissas e resultados de valuation com dados e fórmulas auditáveis.
            </Typography>

            {/* Linha Decorativa */}
            <Box
              sx={{
                width: '72px',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                mt: 4,
              }}
            />
          </Grid>

          {/* Coluna Direita - Formulário de Login */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                maxWidth: '450px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                  textAlign: 'center',
                  mb: 1,
                }}
              >
                Área Restrita
              </Typography>

              <Typography
                variant="body2"
                sx={{ 
                  textAlign: 'center', 
                  mb: 4,
                  color: 'rgba(0, 0, 0, 0.7)', // Contraste melhorado
                  fontWeight: 400,
                }}
              >
                Faça login para acessar a plataforma
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    sx={{
                      ...touchFriendlyTextField,
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.75)', // Label mais escuro
                        fontWeight: 500,
                      },
                      '& .MuiOutlinedInput-input': {
                        color: '#000', // Texto preto
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.5)', // Placeholder mais visível
                        opacity: 1,
                      },
                    }}
                    autoComplete="email"
                    autoFocus
                  />
                  
                  <TextField
                    label="Senha"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    sx={{
                      ...touchFriendlyTextField,
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.75)', // Label mais escuro
                        fontWeight: 500,
                      },
                      '& .MuiOutlinedInput-input': {
                        color: '#000', // Texto preto
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.5)', // Placeholder mais visível
                        opacity: 1,
                      },
                    }}
                    autoComplete="current-password"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      ...touchFriendlyButton,
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #16213e 0%, #0f1419 100%)',
                      },
                    }}
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>

                  <Box
                    sx={{
                      textAlign: 'center',
                      pt: 2,
                      borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <Link
                      component={RouterLink}
                      to="/solicitar-acesso"
                      variant="body2"
                      sx={{
                        textDecoration: 'none',
                        color: '#1a1a2e',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Não tem conta? Solicite acesso
                    </Link>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)', // Melhor contraste
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '&:hover': {
                          color: '#1a1a2e',
                        },
                      }}
                    >
                      Esqueceu a senha?
                    </Link>
                  </Box>
                </Stack>
              </form>
            </Paper>
          </Grid>
        </Grid>
        </Container>
      </Box>
      <AppFooter mode="dark" />
    </Box>
  );
}
